import { useState, useRef, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import Fab from '@mui/material/Fab';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown from 'react-markdown';
import { askDocsQuestion } from './actions';
import {
  getFacilities,
  getSelectedOrganization,
  getSites,
  getUpdates,
} from './selectors';

const PREFIX = 'DocsChat';

const classes = {
  fab: `${PREFIX}-fab`,
  panel: `${PREFIX}-panel`,
  header: `${PREFIX}-header`,
  messages: `${PREFIX}-messages`,
  inputRow: `${PREFIX}-inputRow`,
  question: `${PREFIX}-question`,
  answer: `${PREFIX}-answer`,
};

const Root = styled('div')(({ theme }) => ({
  [`& .${classes.fab}`]: {
    position: 'fixed',
    bottom: theme.spacing(3),
    left: theme.spacing(3),
    zIndex: 1200,
    backgroundColor: '#003C71',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#002a52',
    },
  },

  [`& .${classes.panel}`]: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1200,
    width: 380,
    maxHeight: 500,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    borderRadius: 12,
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      width: 'calc(100vw - 32px)',
      right: 16,
      bottom: 16,
      maxHeight: 'calc(100vh - 100px)',
    },
  },

  [`& .${classes.header}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#003C71',
    color: '#fff',
  },

  [`& .${classes.messages}`]: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    minHeight: 200,
    maxHeight: 340,
  },

  [`& .${classes.inputRow}`]: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    borderTop: '1px solid #e8eaed',
  },

  [`& .${classes.question}`]: {
    backgroundColor: '#e3f2fd',
    borderRadius: 12,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1),
    maxWidth: '85%',
    marginLeft: 'auto',
    wordBreak: 'break-word',
  },

  [`& .${classes.answer}`]: {
    backgroundColor: '#f5f5f5',
    borderRadius: 12,
    padding: theme.spacing(1, 1.5),
    marginBottom: theme.spacing(1.5),
    maxWidth: '85%',
    wordBreak: 'break-word',
    fontSize: '0.875rem',
    '& p': { margin: '0 0 8px 0' },
    '& p:last-child': { marginBottom: 0 },
    '& ul, & ol': { margin: '4px 0', paddingLeft: 20 },
    '& code': {
      backgroundColor: '#e8eaed',
      padding: '1px 4px',
      borderRadius: 3,
      fontSize: '0.8rem',
    },
    '& strong': { fontWeight: 600 },
  },
}));

function getEnabledModes(modes) {
  if (!modes) return [];
  return Object.entries(modes)
    .filter(([, v]) => v && v.enabled)
    .map(([k]) => k.toUpperCase());
}

export default function DocsChat() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const organization = useSelector(getSelectedOrganization);
  const facilities = useSelector(getFacilities);
  const updates = useSelector(getUpdates);
  const sites = useSelector(getSites);

  const context = useMemo(() => {
    if (!organization) return null;
    return {
      organization: organization.org_name,
      sites: (sites || []).map((s) => ({ name: s.site_name, id: s.site_id })),
      facilities: (facilities || []).map((f) => {
        const latest = updates?.[f.facilityId]?.[0];
        return {
          name: f.facilityDesc,
          type: f.facilityType || 'pond',
          siteId: f.siteId,
          stageDesired: f.stageDesired,
          stageMinimum: f.stageMinimum,
          stageMaximum: f.stageMaximum,
          enabledModes: getEnabledModes(f.modes),
          current: latest
            ? {
                stage: latest.stage,
                target: latest.target,
                valve: latest.valve,
                valveReason: latest.valveReason,
                mode: latest.mode,
                timestamp: latest.timestamp,
              }
            : null,
        };
      }),
    };
  }, [organization, facilities, updates, sites]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || loading) return;

    setInput('');
    setMessages((prev) => [...prev, { type: 'question', text: question }]);
    setLoading(true);

    try {
      const answer = await askDocsQuestion(question, context);
      setMessages((prev) => [...prev, { type: 'answer', text: answer }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          type: 'answer',
          text: 'Sorry, something went wrong. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Root>
      {!open && (
        <Fab
          className={classes.fab}
          onClick={() => setOpen(true)}
          aria-label="Ask SmartSWM"
          size="medium"
        >
          <HelpOutlineIcon />
        </Fab>
      )}
      {open && (
        <Paper className={classes.panel}>
          <div className={classes.header}>
            <Typography variant="subtitle1" fontWeight={600}>
              Ask SmartSWM
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              sx={{ color: '#fff' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          <div className={classes.messages}>
            {messages.length === 0 && !loading && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', mt: 4 }}
              >
                Ask a question about SmartSWM
              </Typography>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={classes[msg.type]}>
                {msg.type === 'answer' ? (
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
                ) : (
                  <Typography variant="body2">{msg.text}</Typography>
                )}
              </div>
            ))}
            {loading && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: 8,
                }}
              >
                <CircularProgress size={24} />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <form className={classes.inputRow} onSubmit={handleSubmit}>
            <TextField
              fullWidth
              size="small"
              placeholder="Ask a question..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              autoFocus
              variant="outlined"
              sx={{ mr: 1 }}
            />
            <IconButton
              type="submit"
              disabled={loading || !input.trim()}
              color="primary"
            >
              <SendIcon />
            </IconButton>
          </form>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              textAlign: 'right',
              p: 0.75,
              borderTop: '1px solid #e8eaed',
            }}
          >
            Email a human at{' '}
            <a href="mailto:support@smartswm.com" style={{ color: '#003C71' }}>
              support@smartswm.com
            </a>
          </Typography>
        </Paper>
      )}
    </Root>
  );
}
